import { useEffect, useRef, useState } from 'react'
import { ServerApiClient } from '../../data/ServerApiClient'
import {
  markDemoSession,
  sanitizeDemoRedirect,
  setDemoOwnerHome,
  setDemoPersona,
} from '../../data/demoSession'
import { buildCfoRoutePath } from '../parity-dev/cfo/cfoRoutes'
import { TOUR_FORWARD_PARAMS } from '../tour/tourSession'
import type { DemoCompany, CeoIndustryKey } from '../parity-dev/cfo/ceo/ceoCockpitTypes'
import { loadBundledTour } from '../tour/tourLoader'
import { DEFAULT_OWNER_DEMO_SLUG } from '../parity-dev/cfo/ceo/ceoCockpitTypes'
import { isCfoPageSlug } from '../parity-dev/cfo/cfoRoutes'

const INDUSTRY_KEYWORDS: Record<CeoIndustryKey, string[]> = {
  hvac: ['beacon', 'hvac', 'mechanical'],
  restaurant: ['cedar', 'oak', 'restaurant'],
  law: ['fenwick', 'law'],
  saas: ['larkspur', 'analytics', 'saas'],
  distributor: ['alder', 'ridge', 'outfitter', 'distributor'],
}

/**
 * `/demo` — one-click entry into the live product demo.
 *
 * Default (no persona): mints a shared read-only session and lands on the
 * Rivertown Advisory firm cockpit, or a `?to=` deep link.
 *
 * Owner persona (`?persona=owner` or `/demo/owner`): lands on a company's
 * CEO cockpit. `?company=hvac|restaurant|law|saas|distributor` picks the books
 * (default distributor = Alder Ridge).
 */
/**
 * Carry tour params across the redirect.
 *
 * `/demo` finishes with `window.location.replace(path)`, which drops the query
 * string entirely — so `?fg_tour=...` set on the marketing CTA would never
 * reach the app, and the tour would silently never start. Re-attach the
 * allow-listed params to the destination instead.
 */
function withTourParams(path: string, params: URLSearchParams): string {
  const carried = new URLSearchParams()
  for (const key of TOUR_FORWARD_PARAMS) {
    const value = params.get(key)
    if (value) carried.set(key, value)
  }
  // Storylane-compatible personalization: ?token[first_name]=Jane
  params.forEach((value, key) => {
    if (/^token\[[a-z0-9_-]+\]$/i.test(key)) carried.set(key, value)
  })
  const qs = carried.toString()
  if (!qs) return path
  return path.includes('?') ? `${path}&${qs}` : `${path}?${qs}`
}

export function CfoDemoEntryPage() {
  const [error, setError] = useState<string>('')
  const startedRef = useRef(false)

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    const api = new ServerApiClient()
    api
      .demoSession()
      .then(async (out) => {
        if (out?.status === 'authenticated' && out.access_token) {
          markDemoSession(out.session_id)
          const params = new URLSearchParams(window.location.search)
          const persona = readPersona(params)
          if (persona === 'firm') {
            // Record it so the session knows which surface it is on. The
            // route below is unchanged — the firm cockpit is already where
            // an unrecognised persona lands — but the intent is now stated
            // rather than inferred from a default.
            setDemoPersona('firm')
          }
          if (persona === 'owner') {
            setDemoPersona('owner')
            const industry = readIndustry(params.get('company'), params)
            const page = readOwnerPage(params.get('to'))
            const path = await resolveOwnerHome(api, industry, page)
            setDemoOwnerHome(path)
            window.location.replace(withTourParams(path, params))
            return
          }
          const rawTo = params.get('to') || ''
          const aliasMatch = /^cfo:([a-z0-9_-]+)$/i.exec(rawTo.trim())
          if (aliasMatch) {
            try {
              const portfolio = await api.getFirmPortfolio('rivertown-advisory')
              const clients = portfolio?.clients || []
              const anchor =
                clients.find((client) =>
                  `${client.company_name || ''} ${client.client_name || ''}`.toLowerCase().includes('beacon'),
                ) || clients[0]
              if (anchor?.workbook_id) {
                window.location.replace(
                  withTourParams(
                    `/${encodeURIComponent(anchor.site_slug || 'rivertown-advisory')}/workbooks/${encodeURIComponent(anchor.workbook_id)}/cfo/${encodeURIComponent(aliasMatch[1].toLowerCase())}`,
                    params,
                  ),
                )
                return
              }
            } catch {
              /* fall through to the cockpit */
            }
          }
          window.location.replace(withTourParams(sanitizeDemoRedirect(rawTo), params))
        } else {
          setError('The live demo is not available right now.')
        }
      })
      .catch((err: any) => {
        const status = Number(err?.status ?? NaN)
        setError(
          status === 429
            ? 'The demo is busy right now — try again in a minute.'
            : 'The live demo is not available right now.',
        )
      })
  }, [])

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f6f4ef',
        color: '#1b2a33',
        fontFamily: 'Inter, system-ui, sans-serif',
        padding: 24,
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 440 }}>
        {error ? (
          <>
            <h1 style={{ fontSize: 20, margin: '0 0 8px' }}>{error}</h1>
            <p style={{ color: '#5b6b74', margin: '0 0 16px' }}>
              You can still start a free trial on your own books — no credit card.
            </p>
            <a
              href="/signup?plan=pro"
              style={{
                display: 'inline-block',
                background: '#1b4965',
                color: '#fffdf8',
                borderRadius: 8,
                padding: '10px 18px',
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              Start free
            </a>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: 20, margin: '0 0 8px' }}>Opening the live demo…</h1>
            <p style={{ color: '#5b6b74', margin: 0 }}>
              Loading Rivertown Advisory — a mixed-provider sample firm with QuickBooks, Xero, Sage, and NetSuite clients.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * A tour's own `entry` block is the authority on where that tour starts.
 *
 * The bug this fixes: /demo?fg_tour=cfo-first-look with no other params landed
 * on the FIRM cockpit while the narration described Beacon Mechanical's HVAC
 * books — the marketing embed builds exactly that URL. Two sources of truth
 * about where a tour begins, and the anchor verifier never caught it because
 * IT reads doc.entry. Now the entry page reads doc.entry too, so every entry
 * point agrees: the embed, a share link, and a bare pasted URL.
 *
 * Explicit params still win — an author testing `&company=law` means it.
 */
function tourEntry(params: URLSearchParams): { persona?: string | null; company?: string | null } | null {
  const slug = String(params.get('fg_tour') || '').trim()
  if (!slug) return null
  try {
    return loadBundledTour(slug)?.doc.entry ?? null
  } catch {
    return null
  }
}

/**
 * Which surface this visit is about.
 *
 * 'firm' is recognised explicitly rather than left to the null fall-through.
 * The advisor path happened to work because an unrecognised persona lands on
 * DEMO_ENTRY_PATH, which is the firm cockpit — so an advisor tour was one
 * default-route change away from silently ejecting people onto the wrong
 * surface, with nothing to point at when it did.
 */
function readPersona(params: URLSearchParams): 'owner' | 'firm' | null {
  const fromQuery = String(params.get('persona') || '').trim().toLowerCase()
  if (fromQuery === 'owner') return 'owner'
  if (fromQuery === 'firm') return 'firm'
  const path = String(window.location.pathname || '')
  if (path === '/demo/owner' || path.endsWith('/demo/owner')) return 'owner'
  if (path === '/demo/firm' || path.endsWith('/demo/firm')) return 'firm'
  const fromTour = String(tourEntry(params)?.persona || '').trim().toLowerCase()
  if (fromTour === 'owner') return 'owner'
  if (fromTour === 'firm') return 'firm'
  return null
}

function readIndustry(raw: string | null, params?: URLSearchParams): CeoIndustryKey {
  const explicit = String(raw || '').trim().toLowerCase()
  const fromTour = params ? String(tourEntry(params)?.company || '').trim().toLowerCase() : ''
  const value = explicit || fromTour
  if (value === 'hvac' || value === 'restaurant' || value === 'law' || value === 'saas' || value === 'distributor') {
    return value
  }
  return 'distributor'
}

function readOwnerPage(rawTo: string | null): string {
  const aliasMatch = /^cfo:([a-z0-9_-]+)$/i.exec(String(rawTo || '').trim())
  return aliasMatch ? aliasMatch[1].toLowerCase() : 'ceo'
}

async function resolveOwnerHome(
  api: ServerApiClient,
  industry: CeoIndustryKey,
  page: string,
): Promise<string> {
  try {
    const roster = await api.getDemoCompanies()
    const companies = roster?.companies || []
    const picked = pickDemoCompany(companies, industry)
    if (picked?.workbook_id) {
      return buildCfoRoutePath({
        siteSlug: picked.site_slug || 'rivertown-advisory',
        workbookId: picked.workbook_id,
        page: isCfoPageSlug(page) ? page : 'ceo',
      })
    }
  } catch {
    /* fall through to the firm portfolio */
  }
  const portfolio = await api.getFirmPortfolio('rivertown-advisory')
  const clients = portfolio?.clients || []
  const keywords = INDUSTRY_KEYWORDS[industry] || INDUSTRY_KEYWORDS.distributor
  const match =
    clients.find((client) => {
      const hay = `${client.company_name || ''} ${client.client_name || ''}`.toLowerCase()
      return keywords.some((keyword) => hay.includes(keyword))
    }) ||
    clients.find((client) => `${client.company_name || ''}`.toLowerCase().includes('alder')) ||
    clients[0]
  if (!match?.workbook_id) return '/rivertown-advisory/firm'
  return buildCfoRoutePath({
    siteSlug: match.site_slug || 'rivertown-advisory',
    workbookId: match.workbook_id,
    page: 'ceo',
  })
}

function pickDemoCompany(companies: DemoCompany[], industry: CeoIndustryKey): DemoCompany | null {
  const exact = companies.find((company) => company.industry_key === industry)
  if (exact) return exact
  return companies.find((company) => company.slug === DEFAULT_OWNER_DEMO_SLUG) || companies[0] || null
}
